import { useEffect, useRef, useState } from 'react';
import { Icon } from './components';

export type AddressLookupResult = {
  id: string;
  label: string;
  line1: string;
  line2?: string;
  city: string;
  state: string;
  postalCode: string;
  country?: string;
  placeId?: string;
  secondary?: string;
};

export type PharmacyLookupResult = {
  id: string;
  label: string;
  name: string;
  address?: string;
  phone?: string;
};

type LookupResponse<T> = {
  results?: T[];
  result?: T | null;
  attribution?: string;
  unavailable?: boolean;
  configured?: boolean;
};

type AddressSearchInputProps = {
  value: string;
  onInput: (value: string) => void;
  onSelect: (value: AddressLookupResult) => void;
  search: (query: string, sessionToken: string) => Promise<LookupResponse<AddressLookupResult>>;
  resolve?: (placeId: string, sessionToken: string) => Promise<LookupResponse<AddressLookupResult>>;
  placeholder?: string;
  disabled?: boolean;
};

function createAddressSessionToken() {
  const cryptoApi = window.crypto as Crypto & { randomUUID?: () => string };
  if (typeof cryptoApi?.randomUUID === 'function') return cryptoApi.randomUUID();
  return `address-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
}

export function AddressSearchInput({ value, onInput, onSelect, search, resolve, placeholder = 'Start typing a street address', disabled = false }: AddressSearchInputProps) {
  const [results, setResults] = useState<AddressLookupResult[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [unavailable, setUnavailable] = useState(false);
  const [attribution, setAttribution] = useState('');
  const sequence = useRef(0);
  const selectedLine = useRef('');
  const userEdited = useRef(false);
  const sessionToken = useRef('');
  const searchRef = useRef(search);
  const resolveRef = useRef(resolve);
  useEffect(() => { searchRef.current = search; }, [search]);
  useEffect(() => { resolveRef.current = resolve; }, [resolve]);

  const activeSessionToken = () => {
    if (!sessionToken.current) sessionToken.current = createAddressSessionToken();
    return sessionToken.current;
  };

  useEffect(() => {
    const query = String(value || '').trim();
    if (disabled || !userEdited.current || query.length < 3 || query === selectedLine.current) {
      setResults([]);
      setOpen(false);
      setLoading(false);
      return;
    }
    const requestId = ++sequence.current;
    const timer = window.setTimeout(() => {
      setLoading(true);
      setUnavailable(false);
      const token = activeSessionToken();
      void searchRef.current(query, token).then((response) => {
        if (requestId !== sequence.current) return;
        const next = Array.isArray(response?.results) ? response.results : [];
        setResults(next);
        setAttribution(response?.attribution || '');
        setUnavailable(Boolean(response?.unavailable));
        setOpen(true);
      }).catch(() => {
        if (requestId !== sequence.current) return;
        setResults([]);
        setUnavailable(true);
        setOpen(true);
      }).finally(() => {
        if (requestId === sequence.current) setLoading(false);
      });
    }, 260);
    return () => window.clearTimeout(timer);
  }, [value, disabled]);

  const finishSelection = (item: AddressLookupResult) => {
    userEdited.current = false;
    selectedLine.current = item.line1;
    setResults([]);
    setOpen(false);
    setUnavailable(false);
    sessionToken.current = '';
    onSelect(item);
  };

  const choose = (item: AddressLookupResult) => {
    const resolver = resolveRef.current;
    if (!item.placeId || !resolver) {
      finishSelection(item);
      return;
    }
    const token = activeSessionToken();
    setOpen(false);
    setLoading(true);
    setUnavailable(false);
    void resolver(item.placeId, token).then((response) => {
      const resolved = response?.result;
      if (resolved?.line1) {
        finishSelection(resolved);
        return;
      }
      selectedLine.current = item.line1;
      sessionToken.current = '';
      onInput(item.line1);
      setUnavailable(true);
    }).catch(() => {
      selectedLine.current = item.line1;
      sessionToken.current = '';
      onInput(item.line1);
      setUnavailable(true);
    }).finally(() => setLoading(false));
  };

  return <div className="lookup-combobox address-search-combobox">
    <div className="lookup-input-wrap">
      <input
        value={value}
        disabled={disabled}
        autoComplete="off"
        placeholder={placeholder}
        onChange={(event) => {
          userEdited.current = true;
          selectedLine.current = '';
          onInput(event.target.value);
        }}
        onFocus={() => { if (userEdited.current && (results.length || unavailable)) setOpen(true); }}
        onBlur={() => window.setTimeout(() => setOpen(false), 140)}
      />
      {loading && <span className="lookup-input-icon" aria-hidden="true"><Icon name="refresh" /></span>}
    </div>
    {open && <div className="lookup-popover" role="listbox" aria-label="Address suggestions">
      {results.map((item) => {
        const locality = [item.city, [item.state, item.postalCode].filter(Boolean).join(' ')].filter(Boolean).join(', ');
        return <button type="button" role="option" key={item.id} onMouseDown={(event) => event.preventDefault()} onClick={() => choose(item)}>
          <span className="lookup-result-icon"><Icon name="locations" /></span>
          <span><strong>{item.line1}</strong><small>{item.secondary || locality || item.label}</small></span>
        </button>;
      })}
      {!results.length && !loading && <div className="lookup-empty"><strong>{unavailable ? 'Address search is unavailable.' : 'No address matches yet.'}</strong><small>Continue entering the address manually.</small></div>}
      {attribution && <small className="lookup-attribution">{attribution}</small>}
    </div>}
  </div>;
}

type PharmacySearchInputProps = {
  value: string;
  onChange: (value: string) => void;
  search: (query: string) => Promise<LookupResponse<PharmacyLookupResult>>;
  placeholder?: string;
  disabled?: boolean;
};

export function PharmacySearchInput({ value, onChange, search, placeholder = 'Search pharmacy name, city, or ZIP', disabled = false }: PharmacySearchInputProps) {
  const [results, setResults] = useState<PharmacyLookupResult[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [unavailable, setUnavailable] = useState(false);
  const [attribution, setAttribution] = useState('');
  const sequence = useRef(0);
  const selectedValue = useRef('');
  const userEdited = useRef(false);
  const searchRef = useRef(search);
  useEffect(() => { searchRef.current = search; }, [search]);

  useEffect(() => {
    const query = String(value || '').trim();
    if (disabled || !userEdited.current || query.length < 2 || query === selectedValue.current) {
      setResults([]);
      setOpen(false);
      setLoading(false);
      return;
    }
    const requestId = ++sequence.current;
    const timer = window.setTimeout(() => {
      setLoading(true);
      setUnavailable(false);
      void searchRef.current(query).then((response) => {
        if (requestId !== sequence.current) return;
        setResults(Array.isArray(response?.results) ? response.results : []);
        setAttribution(response?.attribution || '');
        setUnavailable(Boolean(response?.unavailable));
        setOpen(true);
      }).catch(() => {
        if (requestId !== sequence.current) return;
        setResults([]);
        setUnavailable(true);
        setOpen(true);
      }).finally(() => {
        if (requestId === sequence.current) setLoading(false);
      });
    }, 280);
    return () => window.clearTimeout(timer);
  }, [value, disabled]);

  const choose = (item: PharmacyLookupResult) => {
    userEdited.current = false;
    const next = item.label || item.name;
    selectedValue.current = next;
    setResults([]);
    setOpen(false);
    onChange(next);
  };

  return <div className="lookup-combobox pharmacy-search-combobox">
    <div className="lookup-input-wrap">
      <input
        value={value}
        disabled={disabled}
        autoComplete="off"
        placeholder={placeholder}
        onChange={(event) => {
          userEdited.current = true;
          selectedValue.current = '';
          onChange(event.target.value);
        }}
        onFocus={() => { if (userEdited.current && (results.length || unavailable)) setOpen(true); }}
        onBlur={() => window.setTimeout(() => setOpen(false), 140)}
      />
      {loading && <span className="lookup-input-icon" aria-hidden="true"><Icon name="refresh" /></span>}
    </div>
    {open && <div className="lookup-popover pharmacy-lookup-popover" role="listbox" aria-label="Pharmacy suggestions">
      {results.map((item) => <button type="button" role="option" key={item.id} onMouseDown={(event) => event.preventDefault()} onClick={() => choose(item)}>
        <span className="lookup-result-icon"><Icon name="building" /></span>
        <span><strong>{item.name}</strong><small>{item.address || item.phone || 'Pharmacy'}</small>{item.address && item.phone && <em>{item.phone}</em>}</span>
      </button>)}
      {!results.length && !loading && <div className="lookup-empty"><strong>{unavailable ? 'Pharmacy search is unavailable.' : 'No pharmacy matches yet.'}</strong><small>You can still type the pharmacy manually.</small></div>}
      {attribution && <small className="lookup-attribution">{attribution}</small>}
    </div>}
  </div>;
}
